import { useEffect, useState } from "react";
import { useVariant } from "@/hooks/useVariant/useVariant";
import { DeliveryAndGuaranteesMessage } from "@/components/DeliveryAndGuaranteesMessage/DeliveryAndGuaranteesMessage";
import { UserAcknowledgement } from "@/components/UserAcknowledgement/UserAcknowledgement";
import { ProductAccordion } from "@/components/ProductAccordion/ProductAccordion";
import { addM2MProductToBasket } from "../../util/addM2MProductToBasket";
import {
  PrimaryButton,
  UnderlinedButton,
} from "@JohnLewisPartnership/jl-design-system/dist/components/button/Button";
import { DEPRECATED_Message } from "@JohnLewisPartnership/jl-design-system/dist/components/message/Message";
import { Modal } from "@JohnLewisPartnership/jl-design-system/dist/components/modal/Modal";
import { ADD_TO_BASKET_ESI_HIDDEN_OR_VISIBLE } from "@/constants/constants";
import styles from "./MadeToMeasureReviewModal.module.scss";
import { FeatureFlags, Product, RuntimeConfig } from "@/types/types";
import { ConfigurationRequirementsType } from "@/types/prodcatBaseTypes";
import { MadeToMeasureConfiguration } from "../../configurationTypes";
import RomanBlindsSelection from "./RomanBlindsSelection";
import CurtainsSelection from "./CurtainsSelection";
import { formatM2MTitle } from "@/utils/product/formatM2MTitle";
import { dispatchAnalyticsEvent } from "@/utils";
import { getDefaultCategory } from "@/utils/product/getDefaultCategory";
import { ReviewModalBasketSummary } from "./ReviewModalBasketSummary/ReviewModalBasketSummary";
import {
  AddToBasket,
  trackEvent,
} from "@JohnLewisPartnership/jl-analytics-event-stream";

type Props = {
  configuration: MadeToMeasureConfiguration;
  totalPrice: string;
  product: Product;
  runtimeConfig: RuntimeConfig;
  flags: FeatureFlags;
  isOpen: boolean;
  onClose: (e: React.SyntheticEvent) => void;
  configurationReqType: ConfigurationRequirementsType;
};

export const MadeToMeasureReviewModal = ({
  configuration,
  totalPrice,
  product,
  runtimeConfig,
  flags,
  isOpen,
  onClose,
  configurationReqType,
}: Props) => {
  const { variant } = useVariant(product);

  const [isA2bSuccess, setIsA2bSuccess] = useState(false);
  const [isA2bError, setIsA2bError] = useState(false);
  const [userAcknowledgementChecked, setUserAcknowledgementChecked] =
    useState(false);
  const [isA2bSubmitting, setIsA2bSubmitting] = useState(false);
  const [quantityAdded, setQuantityAdded] = useState(0);

  async function onClickAddToBasket() {
    setIsA2bSubmitting(true);

    const { success, commerceItems } = await addM2MProductToBasket(
      runtimeConfig.basketApiBaseUrl,
      1,
      variant.aliases.skuId,
      variant.id,
      configurationReqType,
      configuration,
    );

    setIsA2bSubmitting(false);
    setIsA2bSuccess(success);
    setIsA2bError(!success);

    dispatchAnalyticsEvent(
      `addtobasket${success ? "success" : "error"}`,
      {
        type: getProductType(),
        buttonType: "static",
        qtyRequested: 1,
        product: {
          itemPrice: totalPrice.replace("£", ""),
          productId: product.aliases.pimProductId,
          qty: 1,
          services: [],
          sku: variant.aliases.skuId,
        },
      },
      {},
    );

    trackEvent(AddToBasket, {
      type: getProductType() || undefined,
      buttonType: "static",
      originatingImplementationId: "web-pdp-scaffold-ui",
      triggeringImplementationId: "web-pdp-scaffold-ui",
      page: {
        name: window?.jlData?.page?.pageInfo?.pageName || "",
        type: window?.jlData?.page?.pageInfo?.pageType || "",
      },
      basket: {
        subTotal: window?.jlData?.basket?.subTotal || 0,
        productCount: window?.jlData?.basket?.product?.length || 0,
        quantity:
          window?.jlData?.basket?.product?.reduce(
            (sum: number, prod: { qty?: number }) => sum + +(prod?.qty || 0),
            0,
          ) || 0,
        products:
          window?.jlData?.basket?.product?.map(
            (prod: {
              productId?: string;
              itemPrice?: number;
              qty?: number;
            }) => ({
              id: prod?.productId || "",
              price: +(prod?.itemPrice || 0),
              quantity: +(prod?.qty || 0),
              name: "",
            }),
          ) || [],
      },
      products: [
        {
          id: product.aliases.pimProductId,
          sku: variant.aliases.skuId,
          quantity: 1,
          price: {
            now: parseFloat(totalPrice.replace("£", "")),
          },
          services: [],
        },
      ],
    });

    if (success) {
      if (commerceItems) {
        const itemInBasket = commerceItems.find(
          (commerceitem) => commerceitem.skuId === variant.aliases.skuId,
        );
        if (itemInBasket) setQuantityAdded(itemInBasket.quantity);
      } else {
        setQuantityAdded(1);
      }

      window.dispatchEvent(
        new CustomEvent("jl-events.browser.basketQuantityUpdated"),
      );
    }
  }

  const AddToBasketError = () => {
    return (
      <div data-testid="m2m-a2b-error" className={styles.A2bContainer__error}>
        <DEPRECATED_Message
          type="error"
          body="Sorry, something went wrong. Please try again later"
        />
      </div>
    );
  };

  useEffect(() => {
    const handleUserAcknowledgement = (event: CustomEvent) => {
      const { userAcknowledgementChecked } = event.detail;
      setUserAcknowledgementChecked(userAcknowledgementChecked);
    };

    window.addEventListener(
      ADD_TO_BASKET_ESI_HIDDEN_OR_VISIBLE,
      handleUserAcknowledgement,
    );

    return () => {
      window.removeEventListener(
        ADD_TO_BASKET_ESI_HIDDEN_OR_VISIBLE,
        handleUserAcknowledgement,
      );
    };
  }, []);

  const getSelection = () => {
    if (
      configurationReqType === "MadeToMeasureRomanBlind" &&
      configuration.type === "RomanBlind"
    ) {
      return (
        <RomanBlindsSelection configuration={configuration} onClose={onClose} />
      );
    }
    if (
      configurationReqType === "MadeToMeasureCurtain" &&
      configuration.type === "Curtain"
    ) {
      return (
        <CurtainsSelection configuration={configuration} onClose={onClose} />
      );
    }
    return null;
  };

  const getTitle = () => {
    switch (configurationReqType) {
      case "MadeToMeasureCurtain":
        return "Curtain";
      case "MadeToMeasureRomanBlind":
        return "Roman Blind";
      default:
        return "";
    }
  };

  const getProductType = () => {
    if (configurationReqType === "MadeToMeasureRomanBlind") {
      return "m2mroman";
    }

    if (configurationReqType === "MadeToMeasureCurtain") {
      return "m2mcurtain";
    }

    return "";
  };

  const getHeadingType = () => {
    if (configuration.type === "Curtain") {
      const config = configuration;

      if (config.headingType.includes("Pencil")) return "PP";
      if (config.headingType.includes("Double")) return "DP";
      if (config.headingType.includes("Triple")) return "TP";
      if (config.headingType.includes("Eyelet")) return "EL";
    }
    return "";
  };

  const getImgUrl = () => {
    switch (configurationReqType) {
      case "MadeToMeasureCurtain":
        return variant.images.alternatives.find((img) =>
          img.endsWith(`WS${getHeadingType()}`),
        );
      case "MadeToMeasureRomanBlind":
        return variant.images.alternatives.find((img) => img.endsWith("WSRB"));
      default:
        return "";
    }
  };

  const getPlpUrl = (): string => {
    const category =
      product.browsePageHierarchy[product.browsePageHierarchy.length - 1];
    return category && "url" in category ? category.url : "";
  };

  const productTitle = getTitle();
  const plpUrl = getPlpUrl();
  const imageUrl = getImgUrl();
  const selection = getSelection();

  useEffect(() => {
    dispatchAnalyticsEvent(
      "global.impression",
      {
        type: "pdp",
        subType: "m2m summary",
        products: [
          {
            brand: product.brand?.name || "",
            category: getDefaultCategory(product),
            sku: variant.aliases?.skuId,
            price: variant.price?.value,
            productName: variant.title,
            productId: product.aliases?.pimProductId,
          },
        ],
      },
      {},
    );
  }, []);

  return (
    <Modal
      variant="full-screen"
      title={`Your Made to Measure ${productTitle} summary`}
      footer={false}
      isOpen={isOpen}
      onClose={onClose}
      className={styles.ReviewModal}
    >
      <div
        data-testid="m2m-review-container"
        className={styles.ReviewContainer}
      >
        <div data-testid="m2m-review-content" className={styles.ReviewContent}>
          <div
            data-testid="m2m-review-column-left"
            className={styles.ReviewContent__columnLeft}
          >
            <h2 className={styles.ReviewContent__heading}>Your selection</h2>
            <ul
              data-testid="m2m-review-config-list"
              className={styles.ReviewContent__list}
            >
              <li data-testid="m2m-review-config-fabric">
                <img
                  className={styles.ReviewContent__image}
                  src={variant.images.primary}
                  data-testid="m2m-review-config-fabric-image"
                />
                <div>
                  <p className={styles.ReviewContent__title}>Fabric</p>
                  <h2 data-testid="m2m-review-config-fabric-description">
                    {formatM2MTitle(
                      variant.title,
                      configurationReqType === "MadeToMeasureRomanBlind",
                    )}{" "}
                    <UnderlinedButton
                      tag="a"
                      type="button"
                      href={plpUrl}
                      labelText="Change fabric"
                    />
                  </h2>
                </div>
              </li>
              {selection}
            </ul>
          </div>
          <div
            data-testid="m2m-review-column-right"
            className={styles.ReviewContent__columnRight}
          >
            <div
              data-testid="m2m-review-product-details"
              className={styles.ReviewContent__product}
            >
              <div className={styles.ReviewContent__product__image}>
                <img src={imageUrl} data-testid="m2m-review-product-image" />
              </div>
              <div
                data-testid="m2m-review-product-info"
                className={styles.ReviewContent__product__info}
              >
                <h1
                  className={styles.ReviewContent__product__title}
                  data-testid="m2m-review-product-title"
                >
                  {formatM2MTitle(
                    variant.title,
                    configurationReqType === "MadeToMeasureRomanBlind",
                  )}
                </h1>
                <h2 className={styles.ReviewContent__heading}>
                  {`Total ${productTitle} price`}
                </h2>
                <h2
                  className={styles.ReviewContent__product__price}
                  data-testid="m2m-review-product-price"
                >
                  {totalPrice}
                </h2>
              </div>
            </div>
            <div data-testid="m2m-review-delivery-guarantee">
              <DeliveryAndGuaranteesMessage
                product={product}
                configProps={{
                  messageList: ["includedGuarantees", "deliveryHighlights"],
                }}
                flags={flags}
              />
            </div>
            <div
              className={styles.A2bContainer}
              data-testid="m2m-review-a2b-container"
            >
              <div
                className={styles.A2bContainer__userAcknowledgement}
                data-testid="m2m-review-a2b-user-acknowledgement"
              >
                <UserAcknowledgement product={product} />
              </div>
              <div
                className={styles.A2bContainer__a2bButton}
                data-testid="m2m-review-a2b-cta-section"
              >
                <p>{`Total ${productTitle} price`}</p>
                <p
                  className={styles.A2bContainer__price}
                  data-testid="m2m-review-a2b-cta-price"
                >
                  {totalPrice}
                </p>
                <PrimaryButton
                  tag="button"
                  type="button"
                  disabled={!userAcknowledgementChecked}
                  submitting={isA2bSubmitting}
                  onClick={onClickAddToBasket}
                  data-testid="m2m-review-a2b-cta-button"
                  className={styles.A2bContainer__cta}
                  labelText="Add to your basket"
                  width="full-width"
                />
                {isA2bSuccess && (
                  <ReviewModalBasketSummary
                    quantity={quantityAdded}
                    title={variant.title}
                    configReqType={configurationReqType}
                    configuration={configuration}
                  />
                )}
              </div>
              {isA2bError && <AddToBasketError />}
            </div>
          </div>

          <div
            data-testid="m2m-preview-delivery-details"
            className={styles.ReviewContent__columnFull}
          >
            <ProductAccordion
              product={product}
              runtimeConfig={runtimeConfig}
              experiencesRawObject={[]}
              configProps={{
                accordionConfig: [
                  {
                    name: "deliveryOptions",
                    open: false,
                  },
                ],
              }}
              flags={flags}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};
